/**
 * PortalLeadExport — CSV export + API documentation page
 *
 * Section 1: Download leads as CSV with time range, vertical, and status filters.
 * Section 2: REST API documentation with curl examples and copy buttons.
 */

import React, { useState, useEffect, useCallback } from 'react';
import PortalLayout from '@/components/portal/PortalLayout';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { supabaseGet } from '@/utils/supabaseRest';
import { useAuth } from '@/contexts/AuthContext';
import {
  Download,
  FileSpreadsheet,
  Code2,
  Copy,
  CheckCircle2,
  Loader2,
  BookOpen,
} from 'lucide-react';

// ── Types ─────────────────────────────────────────────────────────────

interface Vertical {
  id: string;
  name: string;
}

interface Lead {
  [key: string]: unknown;
}

type TimeRange = '7' | '30' | '90' | 'all';
type StatusFilter = 'all' | 'new' | 'qualified' | 'contacted' | 'converted';

// ── Helpers ───────────────────────────────────────────────────────────

const SUPABASE_URL =
  import.meta.env?.VITE_SUPABASE_URL || 'https://khwvlnkrtrwvfbnnjsrc.supabase.co';

function buildLeadQuery(timeRange: TimeRange, vertical: string, status: StatusFilter): string {
  let query = 'leads?select=*&order=created_at.desc';

  if (timeRange !== 'all') {
    const days = parseInt(timeRange, 10);
    const since = new Date(Date.now() - days * 86_400_000).toISOString();
    query += `&created_at=gte.${since}`;
  }

  if (vertical) {
    query += `&vertical_id=eq.${vertical}`;
  }

  if (status !== 'all') {
    query += `&status=eq.${status}`;
  }

  return query;
}

function leadsToCsv(leads: Lead[]): string {
  if (leads.length === 0) return '';

  const headers = Object.keys(leads[0]);
  const rows = leads.map((lead) =>
    headers
      .map((h) => {
        const val = lead[h];
        const str = val === null || val === undefined ? '' : String(val);
        // Escape quotes and wrap in quotes if contains comma/quote/newline
        if (str.includes(',') || str.includes('"') || str.includes('\n')) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      })
      .join(',')
  );

  return [headers.join(','), ...rows].join('\n');
}

function downloadCsv(csv: string, filename: string) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ── Component ─────────────────────────────────────────────────────────

const PortalLeadExport: React.FC = () => {
  const { user } = useAuth();

  // CSV Export state
  const [timeRange, setTimeRange] = useState<TimeRange>('30');
  const [verticals, setVerticals] = useState<Vertical[]>([]);
  const [selectedVertical, setSelectedVertical] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [exporting, setExporting] = useState(false);
  const [exportCount, setExportCount] = useState<number | null>(null);
  const [loadingVerticals, setLoadingVerticals] = useState(true);

  // Copy state
  const [copied, setCopied] = useState<string | null>(null);

  // Fetch verticals
  useEffect(() => {
    (async () => {
      try {
        const data = await supabaseGet<Vertical[]>(
          'verticals?select=id,name&order=name.asc'
        );
        setVerticals(data);
      } catch (err) {
        console.error('Failed to load verticals:', err);
      } finally {
        setLoadingVerticals(false);
      }
    })();
  }, []);

  const handleExport = useCallback(async () => {
    setExporting(true);
    setExportCount(null);
    try {
      const query = buildLeadQuery(timeRange, selectedVertical, statusFilter);
      const leads = await supabaseGet<Lead[]>(query);
      setExportCount(leads.length);

      if (leads.length === 0) {
        setExporting(false);
        return;
      }

      const csv = leadsToCsv(leads);
      const datePart = new Date().toISOString().slice(0, 10);
      downloadCsv(csv, `intentdrive-leads-${datePart}.csv`);
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setExporting(false);
    }
  }, [timeRange, selectedVertical, statusFilter]);

  const handleCopy = useCallback((text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 1500);
  }, []);

  // API docs snippets
  const anonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY || '<your-anon-key>';
  const tokenRaw = localStorage.getItem('supabase.auth.token');
  const accessToken = tokenRaw ? JSON.parse(tokenRaw).access_token : '<your-access-token>';

  const apiEndpoint = `${SUPABASE_URL}/rest/v1/leads?select=*&order=created_at.desc`;

  const curlExample = `curl -X GET \\
  '${apiEndpoint}' \\
  -H 'apikey: ${anonKey}' \\
  -H 'Authorization: Bearer ${accessToken}'`;

  const headersSnippet = `{
  "apikey": "${anonKey}",
  "Authorization": "Bearer <your-access-token>",
  "Content-Type": "application/json"
}`;

  const filterExamples = `# Filter by status
GET ${SUPABASE_URL}/rest/v1/leads?select=*&status=eq.qualified

# Filter by date range (last 7 days)
GET ${SUPABASE_URL}/rest/v1/leads?select=*&created_at=gte.${new Date(Date.now() - 7 * 86_400_000).toISOString()}

# Filter by vertical
GET ${SUPABASE_URL}/rest/v1/leads?select=*&vertical_id=eq.<vertical-uuid>

# Pagination (limit + offset)
GET ${SUPABASE_URL}/rest/v1/leads?select=*&limit=50&offset=0&order=created_at.desc`;

  return (
    <PortalLayout>
      <div className="p-6 max-w-5xl mx-auto space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Download className="h-6 w-6 text-emerald-400" />
            Lead Export
          </h1>
          <p className="text-slate-400 mt-1">
            Download leads as CSV or integrate via the REST API.
          </p>
        </div>

        {/* ── CSV Export ─────────────────────────────────────────── */}
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-6 space-y-5">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-emerald-400" />
            CSV Export
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Time Range */}
            <div className="space-y-2">
              <Label className="text-slate-300">Time Range</Label>
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value as TimeRange)}
                className="w-full rounded-md border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              >
                <option value="7">Last 7 days</option>
                <option value="30">Last 30 days</option>
                <option value="90">Last 90 days</option>
                <option value="all">All time</option>
              </select>
            </div>

            {/* Vertical Filter */}
            <div className="space-y-2">
              <Label className="text-slate-300">Vertical (optional)</Label>
              {loadingVerticals ? (
                <div className="flex items-center gap-2 text-slate-500 text-sm py-2">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading...
                </div>
              ) : (
                <select
                  value={selectedVertical}
                  onChange={(e) => setSelectedVertical(e.target.value)}
                  className="w-full rounded-md border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                >
                  <option value="">All verticals</option>
                  {verticals.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.name}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Status Filter */}
            <div className="space-y-2">
              <Label className="text-slate-300">Status</Label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
                className="w-full rounded-md border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              >
                <option value="all">All statuses</option>
                <option value="new">New</option>
                <option value="qualified">Qualified</option>
                <option value="contacted">Contacted</option>
                <option value="converted">Converted</option>
              </select>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <Button
              onClick={handleExport}
              disabled={exporting}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {exporting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Exporting...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Download CSV
                </>
              )}
            </Button>

            {exportCount !== null && (
              <span className="text-sm text-slate-400">
                {exportCount === 0 ? (
                  'No leads matched your filters.'
                ) : (
                  <>
                    <CheckCircle2 className="h-4 w-4 inline mr-1 text-emerald-400" />
                    Exported {exportCount} lead{exportCount !== 1 ? 's' : ''}
                  </>
                )}
              </span>
            )}
          </div>
        </div>

        {/* ── API Documentation ──────────────────────────────────── */}
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-6 space-y-6">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-emerald-400" />
            REST API
          </h2>

          <p className="text-sm text-slate-400">
            Access your leads programmatically via the Supabase PostgREST API.
          </p>

          {/* Endpoint */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-slate-300 text-sm font-semibold uppercase tracking-wide">
                Endpoint
              </Label>
              <button
                onClick={() => handleCopy(apiEndpoint, 'endpoint')}
                className="text-slate-500 hover:text-emerald-400 transition-colors flex items-center gap-1 text-xs"
              >
                <Copy className="h-3.5 w-3.5" />
                {copied === 'endpoint' ? 'Copied' : 'Copy'}
              </button>
            </div>
            <pre className="bg-slate-950 border border-slate-800 rounded p-3 text-xs font-mono text-emerald-400 overflow-x-auto">
              GET {apiEndpoint}
            </pre>
          </div>

          {/* Headers */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-slate-300 text-sm font-semibold uppercase tracking-wide">
                Required Headers
              </Label>
              <button
                onClick={() => handleCopy(headersSnippet, 'headers')}
                className="text-slate-500 hover:text-emerald-400 transition-colors flex items-center gap-1 text-xs"
              >
                <Copy className="h-3.5 w-3.5" />
                {copied === 'headers' ? 'Copied' : 'Copy'}
              </button>
            </div>
            <pre className="bg-slate-950 border border-slate-800 rounded p-3 text-xs font-mono text-slate-300 overflow-x-auto">
              {headersSnippet}
            </pre>
          </div>

          {/* Curl Example */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-slate-300 text-sm font-semibold uppercase tracking-wide">
                Example: curl
              </Label>
              <button
                onClick={() => handleCopy(curlExample, 'curl')}
                className="text-slate-500 hover:text-emerald-400 transition-colors flex items-center gap-1 text-xs"
              >
                <Copy className="h-3.5 w-3.5" />
                {copied === 'curl' ? 'Copied' : 'Copy'}
              </button>
            </div>
            <pre className="bg-slate-950 border border-slate-800 rounded p-3 text-xs font-mono text-slate-300 overflow-x-auto">
              {curlExample}
            </pre>
          </div>

          {/* Filter Examples */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-slate-300 text-sm font-semibold uppercase tracking-wide">
                Common Filters
              </Label>
              <button
                onClick={() => handleCopy(filterExamples, 'filters')}
                className="text-slate-500 hover:text-emerald-400 transition-colors flex items-center gap-1 text-xs"
              >
                <Copy className="h-3.5 w-3.5" />
                {copied === 'filters' ? 'Copied' : 'Copy'}
              </button>
            </div>
            <pre className="bg-slate-950 border border-slate-800 rounded p-4 text-xs font-mono text-slate-300 overflow-x-auto whitespace-pre-wrap">
              {filterExamples}
            </pre>
          </div>
        </div>
      </div>
    </PortalLayout>
  );
};

export default PortalLeadExport;
